import { router } from '@inertiajs/react'
import { useEffect, useRef, useState } from 'react'
import { loadFacebookSdk } from '@/frontend/lib/loadFacebookSdk'

type UseWhatsappEmbeddedSignupOptions = {
  enabled: boolean
  facebookAppId: string | null
  configId: string | null
  apiVersion: string
  connectPath?: string
  digitalWorkerId?: string | null
  returnTo?: string
}

type EmbeddedSignupEventPayload = {
  event: string
  data: Record<string, unknown>
}

const DEFAULT_CONNECT_PATH = '/dashboard/integrations/whatsapp/connect'
const SIGNUP_TIMEOUT_MS = 30_000

function isAllowedFacebookOrigin(origin: string): boolean {
  try {
    const { hostname } = new URL(origin)
    return hostname === 'facebook.com' || hostname.endsWith('.facebook.com')
  } catch {
    return false
  }
}

function parseEmbeddedSignupEvent(rawData: unknown): EmbeddedSignupEventPayload | null {
  let parsed = rawData

  if (typeof rawData === 'string') {
    try {
      parsed = JSON.parse(rawData)
    } catch {
      return null
    }
  }

  if (!parsed || typeof parsed !== 'object') return null

  const payload = parsed as Record<string, unknown>
  if (payload.type !== 'WA_EMBEDDED_SIGNUP' || typeof payload.event !== 'string') return null

  return {
    event: payload.event,
    data: typeof payload.data === 'object' && payload.data !== null ? payload.data as Record<string, unknown> : {},
  }
}

export function useWhatsappEmbeddedSignup({
  enabled,
  facebookAppId,
  configId,
  apiVersion,
  connectPath = DEFAULT_CONNECT_PATH,
  digitalWorkerId,
  returnTo,
}: UseWhatsappEmbeddedSignupOptions) {
  const [error, setError] = useState<string | null>(null)
  const [isSdkLoading, setIsSdkLoading] = useState(false)
  const [isConnecting, setIsConnecting] = useState(false)
  const signupTimeoutRef = useRef<number | null>(null)
  const submitOptionsRef = useRef({ connectPath, digitalWorkerId, returnTo })
  const signupRef = useRef({
    authCode: null as string | null,
    wabaId: null as string | null,
    phoneNumberId: null as string | null,
    submitted: false,
  })

  const clearSignupTimeout = () => {
    if (signupTimeoutRef.current !== null) {
      window.clearTimeout(signupTimeoutRef.current)
      signupTimeoutRef.current = null
    }
  }

  useEffect(() => {
    submitOptionsRef.current = { connectPath, digitalWorkerId, returnTo }
  }, [connectPath, digitalWorkerId, returnTo])

  const resetSignup = () => {
    clearSignupTimeout()
    signupRef.current = {
      authCode: null,
      wabaId: null,
      phoneNumberId: null,
      submitted: false,
    }
  }

  const finishSignup = () => {
    setIsConnecting(false)
    setIsSdkLoading(false)
    resetSignup()
  }

  const failSignup = (message: string) => {
    setError(message)
    finishSignup()
  }

  const submitSignup = () => {
    const { authCode, wabaId, phoneNumberId, submitted } = signupRef.current
    if (!authCode || !wabaId || submitted) return

    signupRef.current.submitted = true
    clearSignupTimeout()

    const latestOptions = submitOptionsRef.current
    const payload: Record<string, string | null> = {
      code: authCode,
      waba_id: wabaId,
      phone_number_id: phoneNumberId,
      return_to: latestOptions.returnTo ?? window.location.pathname + window.location.search,
    }
    if (latestOptions.digitalWorkerId) payload.digital_worker_id = latestOptions.digitalWorkerId

    router.post(latestOptions.connectPath, payload, {
      preserveScroll: true,
      onFinish: finishSignup,
    })
  }

  useEffect(() => {
    const sessionInfoListener = (event: MessageEvent) => {
      if (!isAllowedFacebookOrigin(event.origin)) return

      const signupEvent = parseEmbeddedSignupEvent(event.data)
      if (!signupEvent) return

      if (signupEvent.event === 'FINISH') {
        const wabaId = typeof signupEvent.data.waba_id === 'string' ? signupEvent.data.waba_id : null
        const phoneNumberId = typeof signupEvent.data.phone_number_id === 'string' ? signupEvent.data.phone_number_id : null

        if (!wabaId) {
          failSignup('A Meta não retornou a WABA selecionada.')
          return
        }

        signupRef.current.wabaId = wabaId
        signupRef.current.phoneNumberId = phoneNumberId
        submitSignup()
        return
      }

      if (signupEvent.event === 'CANCEL') {
        failSignup('A conexão do WhatsApp foi cancelada na Meta.')
      } else if (signupEvent.event === 'ERROR') {
        const errorMessage = typeof signupEvent.data.error_message === 'string'
          ? signupEvent.data.error_message
          : 'A Meta retornou um erro ao iniciar o Embedded Signup.'
        failSignup(errorMessage)
      }
    }

    window.addEventListener('message', sessionInfoListener)

    return () => {
      window.removeEventListener('message', sessionInfoListener)
      clearSignupTimeout()
    }
  }, [])

  const startSignup = async () => {
    if (!enabled || !facebookAppId || !configId) return

    setError(null)
    setIsSdkLoading(true)
    setIsConnecting(true)
    resetSignup()

    try {
      const FB = await loadFacebookSdk({ appId: facebookAppId, version: apiVersion })
      setIsSdkLoading(false)

      FB.login((response) => {
        const authCode = response.authResponse?.code

        if (!authCode) {
          failSignup('Não foi possível concluir a autorização na Meta.')
          return
        }

        signupRef.current.authCode = authCode
        submitSignup()

        if (!signupRef.current.submitted) {
          signupTimeoutRef.current = window.setTimeout(() => {
            failSignup('A Meta não retornou os dados da WABA conectada. Tente novamente.')
          }, SIGNUP_TIMEOUT_MS)
        }
      }, {
        config_id: configId,
        response_type: 'code',
        override_default_response_type: true,
        extras: { sessionInfoVersion: '3' },
      })
    } catch (error) {
      console.error('Error loading Facebook SDK:', error)
      failSignup('Não foi possível carregar o SDK do Facebook.')
    }
  }

  return {
    clearError: () => setError(null),
    error,
    isConnecting,
    isSdkLoading,
    startSignup,
  }
}
